import "./incident.page.css";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Skeleton } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Navbar } from "@src/components/navbar";
import { getCustomerSiteIncidentDetailQuery } from "@src/util/query/incident.query";
import { formatIncidentWhen, getStatusMeta, humanizeKey, isBlankAnswer } from "./incidentMeta";

dayjs.extend(relativeTime);

// Read-only twin of wfc-public's incident page (incidentReportingDetail.page.jsx):
// header, then key facts on the left (Details, Site), the officer's report in the
// middle and the vertical status timeline (every stage New → Closed) on the right.
// Staff-only bits (edit, status updates, share) and links into staff records are
// left out.

const STEPS = ["new", "in-review", "in-progress", "resolved", "closed"];
const OFF_PATH = ["on-hold", "rejected"];

// covered by the sections below - anything else goes in "Other details".
// `address` is listed but never shown - older app builds filled it from the
// officer's first trained site rather than the one picked, so it can belong to
// another site entirely; the Site card shows the incident site's own address instead.
const KNOWN_KEYS = [
    "incidentType", "incidentOutcome", "date", "time", "assignment", "address", "officerName",
    "incidentDetails", "immediateAction", "policeAttended", "policeDetails", "cctv",
    "othersInvolved", "witnesses", "signature",
];

const initials = (name) => (name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const StatusPill = ({ status }) => {
    const s = getStatusMeta(status);
    return <span className="ir-pill" style={{ color: s.color, borderColor: s.color }}>{s.label}</span>;
};

const YesNo = ({ value }) => {
    if (isBlankAnswer(value)) return <span className="ir-muted">Not stated</span>;
    const yes = String(value).trim().toLowerCase() === "yes";
    return <span className={yes ? "ir-flag ir-flag-yes" : "ir-flag"}>{yes ? "Yes" : String(value).trim().toLowerCase() === "no" ? "No" : value}</span>;
};

// "No", "No actions", "No action was taken/required" - an explicit "nothing was
// done", on top of isBlankAnswer's none/nothing/n/a
const NO_ACTION = /^\s*no(\s+(immediate\s+)?actions?(\s+(was|were))?(\s+(taken|required|needed|necessary))?)?\s*[.!]?\s*$/i;

// Answer to the mobile form's "Was any immediate action taken?" - free text, so
// anything that isn't a plain no/none reads as action taken, with the officer's
// own words underneath (a bare "Yes" has nothing more to show)
const ImmediateAction = ({ value }) => {
    const text = value == null ? "" : String(typeof value === "object" ? JSON.stringify(value) : value).trim();
    const taken = !isBlankAnswer(text) && !NO_ACTION.test(text);
    const detail = taken && !/^yes\.?$/i.test(text) ? text : null;
    return (
        <div className={`ir-narrative ir-narrative-secondary ir-action ${taken ? "ir-action-taken" : "ir-action-none"}`}>
            <div className="ir-action-title">
                {taken ? "Action taken" : text ? "No immediate action taken" : "Not stated"}
            </div>
            {detail && <div className="ir-action-text">{detail}</div>}
        </div>
    );
};

const Text = ({ value, empty = "None reported" }) =>
    isBlankAnswer(value) ? <span className="ir-muted">{empty}</span> : <>{typeof value === "object" ? JSON.stringify(value) : String(value)}</>;

const Card = ({ title, extra, children }) => (
    <section className="ir-card">
        <div className="ir-card-head">
            <h3>{title}</h3>
            {extra}
        </div>
        <div className="ir-card-body">{children}</div>
    </section>
);

// Every stage in workflow order, each marked done / current / pending / skipped,
// with on hold / rejected inserted after whichever stage came before them in time.
const buildStages = (timeline) => {
    const chrono = [...timeline].reverse(); // oldest first
    const latest = timeline[0]?.status || "new";
    const eventFor = (status) => chrono.find((t) => t?.status === status);
    let lastReached = 0;
    STEPS.forEach((s, i) => { if (eventFor(s)) lastReached = i; });
    const rejected = !!eventFor("rejected");

    const stages = [];
    STEPS.forEach((status, i) => {
        const event = eventFor(status);
        let state;
        if (event) state = status === latest && status !== "closed" ? "current" : "done";
        else if (i < lastReached) state = "skipped";
        else if (rejected) return; // process ended - nothing still to come
        else state = "pending";
        stages.push({ status, event, state });
    });

    chrono.filter((t) => OFF_PATH.includes(t?.status)).forEach((event) => {
        const at = dayjs(event.createdAt).valueOf();
        let insertAt = 0;
        stages.forEach((st, idx) => { if (st.event && dayjs(st.event.createdAt).valueOf() <= at) insertAt = idx + 1; });
        const state = event.status === "rejected" ? "rejected" : event.status === latest ? "current" : "done";
        stages.splice(insertAt, 0, { status: event.status, event, state });
    });

    return stages;
};

const IncidentTimeline = ({ timeline, reporter }) => {
    const stages = buildStages(timeline);
    let step = 0;
    return (
        <ol className="ir-vt">
            {stages.map((stage) => {
                const s = getStatusMeta(stage.status);
                const onPath = STEPS.includes(stage.status);
                if (onPath) step += 1;
                const event = stage.event;
                return (
                    <li key={stage.status} className={`ir-vt-item ir-vt-${stage.state}`} style={{ "--stage-color": s.color }}>
                        <span className="ir-vt-marker">
                            {stage.state === "done" ? "✓" : stage.state === "rejected" ? "✕" : stage.state === "skipped" ? "–" : onPath ? step : "!"}
                        </span>
                        <div className="ir-vt-body">
                            <div className="ir-vt-head">
                                <span className="ir-vt-label">{s.label}</span>
                                <span className="ir-vt-time">
                                    {event ? dayjs(event.createdAt).format("DD/MM/YYYY HH:mm") : stage.state === "skipped" ? "Skipped" : "Pending"}
                                </span>
                            </div>
                            {stage.status === "new" && event && reporter && <div className="ir-vt-sub">Reported by {reporter}</div>}
                            {event && <div className="ir-vt-sub">{dayjs(event.createdAt).fromNow()}</div>}
                            {event?.comment?.trim() && <div className="ir-vt-note">{event.comment}</div>}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
};

export const IncidentDetail = () => {
    const { incidentId } = useParams();
    const [customerDetail, setCustomerDetail] = useState(null);

    useEffect(() => {
        const detail = localStorage.getItem("customerDetail");
        if (detail) setCustomerDetail(JSON.parse(detail));
    }, []);

    const { data, loading, error } = useQuery(getCustomerSiteIncidentDetailQuery, {
        variables: { id: incidentId },
        fetchPolicy: "cache-and-network",
    });

    const incident = data?.getCustomerSiteIncidentDetail?.response;
    const m = incident?.metadata || {};

    // oldest first from the server; newest first here, always including the
    // report itself (older incidents predate status rows and have none)
    const timeline = useMemo(() => {
        const rows = [...(incident?.timeline || [])];
        if (incident && !rows.some((r) => r.status === "new")) {
            rows.unshift({ _id: "reported", status: "new", comment: "", createdAt: incident.reportedAt });
        }
        return rows.reverse();
    }, [incident]);

    if (!incident) {
        return (
            <div className="ir-page-shell">
                <Navbar customerDetail={customerDetail} />
                <div className="ir-page">
                    <div className="ir-header">
                        {loading && !error
                            ? <Skeleton active paragraph={{ rows: 3 }} />
                            : (
                                <>
                                    <h1 className="ir-title">This incident isn't available</h1>
                                    <div className="ir-meta">It may have been removed, or it isn't on one of your sites.</div>
                                </>
                            )}
                    </div>
                </div>
            </div>
        );
    }

    const latest = timeline[0]?.status || incident.status || "new";
    const reporter = incident.reportedBy;
    const siteName = incident.site?.sitename;
    const reference = `INC-${String(incident._id).slice(-6).toUpperCase()}`;
    const reportedAt = dayjs(incident.reportedAt).format("DD/MM/YYYY HH:mm");
    const occurred = formatIncidentWhen(m.date, m.time);
    const policeAttended = String(m.policeAttended || "").trim().toLowerCase() === "yes";
    // "Police details: No" is just noise when the police never attended
    const showPoliceDetails = policeAttended || !(isBlankAnswer(m.policeDetails) || /^\s*no\.?\s*$/i.test(String(m.policeDetails)));
    const otherKeys = Object.keys(m).filter((k) => !KNOWN_KEYS.includes(k));

    return (
        <div className="ir-page-shell">
            <Navbar customerDetail={customerDetail} />

            <div className="ir-page">
                <div className="ir-print-root">

                    {/* ── Header ── */}
                    <header className="ir-header">
                        <div className="ir-header-top">
                            <div className="ir-header-main">
                                <div className="ir-eyebrow">Incident report <span>{reference}</span></div>
                                <div className="ir-title-row">
                                    <h1 className="ir-title">
                                        {m.incidentType || "Incident"} <span className="ir-title-at">at</span> {siteName || "your site"}
                                    </h1>
                                    <StatusPill status={latest} />
                                </div>
                                <div className="ir-meta">
                                    <span>Occurred <b>{occurred}</b></span>
                                    <span>Reported <b>{reportedAt}</b>{reporter && <> by <b>{reporter}</b></>}</span>
                                    {!isBlankAnswer(m.incidentOutcome) && <span>Outcome <b>{m.incidentOutcome}</b></span>}
                                </div>
                            </div>
                            <div className="ir-header-actions">
                                <button type="button" className="ir-btn ir-print-btn" onClick={() => window.print()}>Print</button>
                            </div>
                        </div>
                    </header>

                    <div className="ir-body">

                        {/* ── Key facts ── */}
                        <aside className="ir-facts-col">

                            <Card title="Site">
                                <div className="ir-site-name">{siteName || "—"}</div>
                                {incident.site?.address && <div className="ir-site-address">{incident.site.address}</div>}
                            </Card>
                            
                            <Card title="Details">
                                <dl className="ir-props">
                                    <dt>Status</dt><dd><StatusPill status={latest} /></dd>
                                    <dt>Reference</dt><dd>{reference}</dd>
                                    <dt>Incident type</dt><dd><Text value={m.incidentType} empty="—" /></dd>
                                    <dt>Outcome</dt><dd><Text value={m.incidentOutcome} empty="—" /></dd>
                                    <dt>Occurred</dt><dd>{occurred}</dd>
                                    <dt>Reported</dt><dd>{reportedAt}</dd>
                                    <dt>Reported by</dt>
                                    <dd>
                                        {reporter ? (
                                            <span className="ir-person">
                                                <span className="ir-avatar">{initials(reporter)}</span>
                                                {reporter}
                                            </span>
                                        ) : "—"}
                                    </dd>
                                </dl>
                            </Card>
                        </aside>

                        {/* ── Report ── */}
                        <div className="ir-main">
                            <Card title="Incident narrative">
                                {/* where the officer was / what they were doing when it happened -
                                    sets the scene, so it leads. Only on reports that have it: the
                                    current mobile form doesn't ask for it. */}
                                {!isBlankAnswer(m.assignment) && (
                                    <>
                                        <div className="ir-label">Assignment</div>
                                        <p className="ir-narrative"><Text value={m.assignment} /></p>
                                    </>
                                )}

                                <div className="ir-label">What happened</div>
                                <p className="ir-narrative"><Text value={m.incidentDetails} empty="No description was given." /></p>

                                <div className="ir-label">Immediate action taken</div>
                                <ImmediateAction value={m.immediateAction} />
                            </Card>

                            <Card title="Police & evidence">
                                <div className="ir-facts">
                                    <div>
                                        <div className="ir-label">Police attended</div>
                                        <YesNo value={m.policeAttended} />
                                    </div>
                                    <div>
                                        <div className="ir-label">CCTV footage</div>
                                        <YesNo value={m.cctv} />
                                    </div>
                                    {showPoliceDetails && (
                                        <div className="ir-facts-wide">
                                            <div className="ir-label">Police details</div>
                                            <div className="ir-value"><Text value={m.policeDetails} /></div>
                                        </div>
                                    )}
                                </div>
                            </Card>

                            <Card title="People involved">
                                <div className="ir-facts ir-facts-2">
                                    <div>
                                        <div className="ir-label">Others involved</div>
                                        <div className="ir-value"><Text value={m.othersInvolved} /></div>
                                    </div>
                                    <div>
                                        <div className="ir-label">Witnesses</div>
                                        <div className="ir-value"><Text value={m.witnesses} /></div>
                                    </div>
                                </div>
                                <div className="ir-declaration">
                                    <div className="ir-label">Officer declaration</div>
                                    <div className="ir-value">
                                        Signed by <b><Text value={m.signature} empty="—" /></b>
                                        <span className="ir-muted"> · submitted {reportedAt}</span>
                                    </div>
                                </div>
                            </Card>

                            {otherKeys.length > 0 && (
                                <Card title="Other details">
                                    <div className="ir-facts ir-facts-2">
                                        {otherKeys.map((key) => (
                                            <div key={key}>
                                                <div className="ir-label">{humanizeKey(key)}</div>
                                                <div className="ir-value"><Text value={m[key]} /></div>
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            )}
                        </div>

                        {/* ── Timeline ── */}
                        <aside className="ir-side">
                            <Card title="Incident timeline">
                                <IncidentTimeline timeline={timeline} reporter={reporter} />
                            </Card>
                        </aside>
                    </div>
                </div>
            </div>
        </div>
    );
};
